import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Alert, Container, Spinner } from "reactstrap";
import axios from "axios";
import { FiArrowRight, FiCheckCircle, FiClock, FiCreditCard, FiMapPin, FiPackage, FiXCircle } from "react-icons/fi";
import Helmet from "../components/Helmet/Helmet";
import { currencyFormatter, getApiUrl, getMediaUrl, siteConfig } from "../utils/site";

const paymentTone = {
  pending: "pending",
  success: "success",
  failed: "failed",
  cancelled: "failed",
};

const OrderSuccess = () => {
  const navigate = useNavigate();
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [orderItems, setOrderItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [paymentStatus, setPaymentStatus] = useState("pending");
  const [polling, setPolling] = useState(true);
  const [hashId, setHashId] = useState(orderId);

  useEffect(() => {
    let interval;

    const fetchOrderAndPayment = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(getApiUrl(`/api/orders/${orderId}`), {
          headers: { Authorization: `Bearer ${token}` },
        });

        const nextOrder = res.data.order;
        const nextItems = res.data.items || [];
        const nextPaymentStatus =
          nextOrder?.payment_status_from_payments ||
          nextOrder?.payment_status ||
          "pending";

        setOrder(nextOrder);
        setOrderItems(nextItems);
        setHashId(nextOrder?.hash_id || orderId);
        setPaymentStatus(nextPaymentStatus);
        setError("");

        if (["success", "failed", "cancelled"].includes(nextPaymentStatus)) {
          setPolling(false);
        }
      } catch (err) {
        setError(err.response?.data?.error || "Failed to fetch payment confirmation.");
        setPolling(false);
      } finally {
        setLoading(false);
      }
    };

    if (orderId) {
      fetchOrderAndPayment();
      interval = setInterval(() => {
        if (polling) fetchOrderAndPayment();
      }, 3500);
    }

    return () => clearInterval(interval);
  }, [orderId, polling]);

  const statusContent = useMemo(() => {
    if (paymentStatus === "success") {
      return {
        icon: <FiCheckCircle />,
        title: "Payment confirmed",
        lead: "Thank you. Your payment has been received and your Roha order is now moving into fulfilment.",
        note: "We will continue preparing your jewellery and delivery updates will appear in your order tracker.",
      };
    }

    if (paymentStatus === "failed" || paymentStatus === "cancelled") {
      return {
        icon: <FiXCircle />,
        title: "Payment not completed",
        lead: "We could not confirm this payment yet.",
        note: "Please try again or contact the Roha team if you need help with your order.",
      };
    }

    return {
      icon: <FiClock />,
      title: "Payment confirmation in progress",
      lead: "We are still waiting for the payment gateway to confirm your transaction.",
      note: "This page refreshes automatically while we check the latest payment status.",
    };
  }, [paymentStatus]);

  return (
    <>
      <Helmet
        title="Payment Status | Roha Jewellery"
        description="Review your Roha Jewellery payment result, order confirmation, and next fulfilment steps."
        canonical={`${siteConfig.url}/payment/success/${orderId}`}
      />

      <main className="roha-page">
        <section className="roha-page-hero roha-page-hero--compact">
          <Container>
            <div className="roha-payment-status">
              <div className={`roha-payment-status__hero is-${paymentTone[paymentStatus] || "pending"}`}>
                <div className="roha-payment-status__icon">{statusContent.icon}</div>
                <p className="roha-eyebrow">Payment journey</p>
                <h1>{statusContent.title}</h1>
                <p className="roha-payment-status__lead">{statusContent.lead}</p>
                <p>{statusContent.note}</p>
                {loading ? <Spinner color="light" /> : null}
                {error ? <Alert color="danger" className="mt-3 mb-0">{error}</Alert> : null}
              </div>

              <div className="roha-payment-status__meta">
                <div className="roha-payment-status__meta-card">
                  <span><FiPackage /> Order reference</span>
                  <strong>{hashId || orderId}</strong>
                </div>
                <div className="roha-payment-status__meta-card">
                  <span><FiCreditCard /> Payment status</span>
                  <strong>{paymentStatus}</strong>
                </div>
                <div className="roha-payment-status__meta-card">
                  <span><FiMapPin /> Delivery status</span>
                  <strong>{order?.delivery_status || "pending"}</strong>
                </div>
              </div>
            </div>
          </Container>
        </section>

        <section className="roha-section">
          <Container>
            <div className="roha-order-review">
              <div className="roha-order-review__summary">
                <div className="roha-checkout-card__header">
                  <div>
                    <p className="roha-eyebrow">Order summary</p>
                    <h2>Your Roha purchase at a glance</h2>
                  </div>
                </div>

                <div className="roha-checkout-summary__list">
                  {orderItems.map((item, index) => (
                    <div key={`${item.product_id}-${index}`} className="roha-order-review__item">
                      <div className="roha-order-review__item-media">
                        {item.image_url ? (
                          <img src={getMediaUrl(item.image_url)} alt={item.product_name || "Product"} />
                        ) : (
                          <div className="roha-order-review__placeholder"><FiPackage /></div>
                        )}
                      </div>
                      <div>
                        <strong>{item.product_name || `Product ${item.product_id}`}</strong>
                        <p>Quantity {item.quantity}</p>
                      </div>
                      <span>{currencyFormatter.format(Number(item.price || 0) * Number(item.quantity || 0))}</span>
                    </div>
                  ))}
                </div>

                {order ? (
                  <div className="roha-checkout-summary__total">
                    <span>Total paid</span>
                    <strong>{currencyFormatter.format(Number(order.total || 0))}</strong>
                  </div>
                ) : null}
              </div>

              <div className="roha-order-review__next">
                <div className="roha-contact-panel">
                  <p className="roha-eyebrow">Next steps</p>
                  <h2>Stay close to your order</h2>
                  <div className="roha-contact-panel__links">
                    <button type="button" onClick={() => navigate(`/orders/${hashId}`)}>
                      <FiPackage />
                      <span>Track this order</span>
                    </button>
                    <button type="button" onClick={() => navigate("/my-profile")}>
                      <FiArrowRight />
                      <span>Open my profile</span>
                    </button>
                    <button type="button" onClick={() => navigate("/")}>
                      <FiCheckCircle />
                      <span>Return to home</span>
                    </button>
                  </div>
                  <div className="roha-contact-panel__note">
                    <FiCreditCard />
                    <p>
                      If your payment remains pending for too long, please contact Roha Jewellery
                      so the team can help verify the transaction.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </Container>
        </section>
      </main>
    </>
  );
};

export default OrderSuccess;
